import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

import { formsV2 as Forms } from '@breezeway/brzw-components';

const ENTER_KEY_CODE = 13;
const BASIC_TIME_FORMAT = 'HH:mma';
const getValidTime = (value, minTime, basicFormat = BASIC_TIME_FORMAT) => {
  let validDate = moment(value, basicFormat);
  if (!value || !validDate.isValid()) return null;
  if (!minTime) return validDate.toDate();

  const shouldUsePm = validDate.isBefore(minTime);
  if (shouldUsePm) validDate = moment(`${value}pm`, `${basicFormat}a`);

  return validDate.isAfter(minTime) && validDate.toDate();
};

class CustomTimePickerInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      inputValue: props.value,
    };
  }

  componentDidUpdate(prevProps) {
    const { value, selectedDate } = this.props;
    if (prevProps.selectedDate !== selectedDate) this.changeInputValue(value);
  }

  componentWillUnmount() {
    this.removeEnterListener();
    this.removeClickListener();
  }

  handleEnter = event => {
    const { onChangeDate, inputRef, highlightedTime } = this.props;

    if (event.keyCode === ENTER_KEY_CODE) {
      onChangeDate(highlightedTime);
      _.invoke(inputRef, 'current.blur');

      if (!highlightedTime) this.changeInputValue('');
    }
  };

  handleClick = event => {
    const { value, wrapperRef, onChangeDate, highlightedTime } = this.props;
    const { inputValue } = this.state;
    const target = _.get(event, 'target');
    const clickedOutsidePicker = !_.invoke(wrapperRef, 'current.contains', target);
    const shouldClearForm = clickedOutsidePicker && _.isEmpty(inputValue) && !_.isEmpty(value);

    if (shouldClearForm) {
      onChangeDate(null);
    } else if (clickedOutsidePicker) {
      onChangeDate(highlightedTime);
    }
  };

  addEnterListener = () => document.addEventListener('keydown', this.handleEnter);

  removeEnterListener = () => document.removeEventListener('keydown', this.handleEnter);

  addClickListener = () => document.addEventListener('mousedown', this.handleClick);

  removeClickListener = () => document.removeEventListener('mousedown', this.handleClick);

  changeInputValue = inputValue => {
    const { minTime, setHighlightedTime } = this.props;

    this.setState({
      inputValue,
    });

    const time = this.getCustomTime(inputValue) || getValidTime(inputValue, minTime);
    setHighlightedTime(time);
  };

  getCustomTime = value => {
    const { minTime } = this.props;

    const withColon = _.includes(value, ':');
    const shouldHandleCustomDateWithoutColon =
      !withColon && (_.size(value) === 3 || _.size(value) === 4);
    const shouldHandleCustomDateWithColon =
      withColon && (_.size(value) === 4 || _.size(value) === 5);
    let time;

    if (shouldHandleCustomDateWithoutColon) {
      const hours = value.slice(-4, -2);
      const mins = value.slice(-2);
      time = `${_.size(hours) > 1 ? '' : '0'}${hours}:${mins}`;
    } else if (shouldHandleCustomDateWithColon) {
      time = `${_.size(value) === 4 ? '0' : ''}${value}`;
    }

    return time && getValidTime(time, minTime);
  };

  onFocusInput = () => {
    const { inputRef, onFocus } = this.props;

    onFocus();
    _.invoke(inputRef, 'current.select');
    this.addEnterListener();
    this.addClickListener();
  };

  onBlurInput = () => {
    const { onBlur, value } = this.props;

    onBlur();
    this.removeEnterListener();
    this.removeClickListener();

    this.changeInputValue(value);
  };

  onClearInput = () => {
    this.props.onChangeDate(null);
  };

  render() {
    const {
      error,
      icon,
      id,
      inputRef,
      label,
      onClick,
      placeholder,
      readOnly,
      required,
      theme,
      withClearAction,
    } = this.props;
    const { inputValue } = this.state;
    const withError = !_.isEmpty(error);

    return (
      <Forms.Input
        additionalSubText={error}
        disabled={readOnly}
        iconAfter={withError && 'info-outline'}
        iconBefore={icon}
        inputProps={{
          id,
          onClick,
        }}
        inputRef={inputRef}
        isRequired={required}
        labelText={label}
        onBlurInput={this.onBlurInput}
        onChange={this.changeInputValue}
        onClearInput={this.onClearInput}
        onFocusInput={this.onFocusInput}
        placeholderText={placeholder}
        size="compact"
        theme={theme}
        value={inputValue}
        withClearAction={withClearAction}
        withError={withError}
      />
    );
  }
}

CustomTimePickerInput.propTypes = {
  error: PropTypes.string,
  highlightedTime: PropTypes.shape({}),
  icon: PropTypes.string,
  id: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  label: PropTypes.string,
  minTime: PropTypes.shape({}),
  onBlur: PropTypes.func,
  onChangeDate: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  selectedDate: PropTypes.shape({}),
  setHighlightedTime: PropTypes.func,
  theme: PropTypes.string,
  value: PropTypes.string,
  withClearAction: PropTypes.bool,
  wrapperRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export default CustomTimePickerInput;
